import React, { useState } from 'react';
import {
  Box,
  ContentTemplate,
  DateOnlySemanticDisplayFormat, PAYLOAD_DATE_FORMAT,
  Progress,
  yesterdayDate
} from 'web-library';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import ControlledAutocomplete from 'web-library/components/form-items/ControlledAutocomplete';
import { UseFormReturn } from 'react-hook-form';
import { CandidateProfileResponseObject } from 'web-library/models/AccountManagementConfiguration';
import ToggleDisplayEditFormItem from '../../../components/ToggleDisplayEditFormItem';
import { useGetIExamMasterCode } from '../../../api-refactored/user/useGetIExamMasterCode';
import { useGetSchoolsByType } from '../../../api-refactored/non-national-exam-registration/useGetSchoolsByType';
import {
  getIExamDropdownItemLabel,
  getIExamMasterCodeSelected,
} from '../../../modules/accountManagement/logic/useCreateCandidateForm';
import {IExamMasterCodeItem, LookupListApiItem, LookupListItem} from '../../../api/user/model';
import NationalityDetails from './NationalityDetails';
import { CreateEditCandidateSchemaData } from './CreateEditCandidateSchema';
import {
  useCandidateNationalityDetails,
  useCandidatePersonalParticulars,
} from './useCreateEditViewCandidateAccountDetails';
import CandidateNameAndIdParticulars from './CandidateNameAndIdParticulars';
import dayjs from 'dayjs';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  halfWidthLeftItem: {
    width: '50%',
    paddingRight: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingRight: 0,
      marginBottom: theme.spacing(2),
    },
  },
  formElementContainer: {
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
    width: '100%',
  },
  halfWidthRightItem: {
    width: '50%',
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: 0,
      marginBottom: theme.spacing(2),
    },
  },
  responsiveFlexContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  keyValueContainer: {
    marginBottom: 0,
  },
  nricFinPassportNo: {
    [theme.breakpoints.between('sm', 637)]: {
      marginTop: theme.spacing(8),
    },
    [theme.breakpoints.between(637, 922)]: {
      marginTop: theme.spacing(5.7),
    },
    [theme.breakpoints.between(922, 'lg')]: {
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.between('md', 1051)]: {
      marginTop: theme.spacing(5.7),
    },
    [theme.breakpoints.up(1051)]: {
      marginTop: theme.spacing(2.9),
    },
  },
}));

export interface CandidatePersonalParticularsProps {
  candidateProfileDetails?: CandidateProfileResponseObject;
  formHook?: UseFormReturn<CreateEditCandidateSchemaData>;
  isEditState: boolean;
  isTaskDetails: boolean;
}

const CandidatePersonalParticulars = ({
  candidateProfileDetails,
  formHook,
  isEditState,
  isTaskDetails,
}: CandidatePersonalParticularsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const { defaultDateOfBirth } = useCandidatePersonalParticulars({
    candidateProfileDetails,
  });

  const { data: iExamsData } = useGetIExamMasterCode();

  const { citizenshipDefaultObject, prStatusDefaultObject } = useCandidateNationalityDetails({
    iExamsData,
    candidateProfileDetails,
  });

  const [dateOfBirth, setDateOfBirth] = useState<string>(defaultDateOfBirth || '');

  const prStatusRenamedCodeValueDescription =
    prStatusDefaultObject?.codeValueDescription === 'ALIEN'
      ? { ...prStatusDefaultObject, codeValueDescription: 'FOREIGNER' }
      : prStatusDefaultObject;

  const [prStatus, setPrStatus] = useState<IExamMasterCodeItem | undefined | {}>(
    prStatusRenamedCodeValueDescription || {}
  );

  const [citizenshipType, setCitizenshipType] = useState<IExamMasterCodeItem | undefined | {}>(
    citizenshipDefaultObject || {}
  );

  const {
    data: dataOrganisation,
    isLoading: isLoadingOrganisation,
    error: errorOrganisation,
    refetch: refetchOrganisation,
  } = useGetSchoolsByType(true, 'NMOE');

  const {
    genderDefaultObject,
    raceDefaultObject,
    occupationDefaultObject,
    schoolDefaultObject,
    IsPersonalParticularsValueUndefined,
    filteredDataOrganisationWithoutMOESchool,
    isMOESchool,
  } = useCandidatePersonalParticulars({
    iExamsData,
    candidateProfileDetails,
    dataOrganisation,
    isTaskDetails,
  });

  if (IsPersonalParticularsValueUndefined) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <Box className={classes.responsiveFlexContainer}>
      <CandidateNameAndIdParticulars
        candidateProfileDetails={candidateProfileDetails}
        formHook={formHook}
        isEditState={isEditState}
        setPrStatus={setPrStatus}
        setCitizenshipType={setCitizenshipType}
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.DateOfBirth')}
        valueText={dayjs(defaultDateOfBirth,PAYLOAD_DATE_FORMAT).format(DateOnlySemanticDisplayFormat)}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledDatePicker
              control={formHook?.control}
              label={t('admin->>UserAccountManagement.DateOfBirth')}
              onChange={(date) => {
                setDateOfBirth(date || '');
                formHook.setValue('dateOfBirth', date || '');
              }}
              inputVariant="filled"
              name="dateOfBirth"
              error={!!formHook.formState.errors?.dateOfBirth}
              helperText={formHook.formState.errors?.dateOfBirth?.message?.toString()}
              isRequired
              maxDate={dayjs(yesterdayDate)}
              disableFuture
              defaultValue={dateOfBirth}
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Gender')}
        valueText={candidateProfileDetails?.gender}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('admin->>UserAccountManagement.Gender')}
              placeholder="Select sex"
              id="input-select-gender"
              control={formHook.control}
              name="gender"
              error={!!formHook.formState.errors?.gender}
              helperText={formHook.formState.errors?.gender?.message?.toString()}
              options={iExamsData ? iExamsData.gender : []}
              defaultValue={genderDefaultObject}
              getOptionLabel={getIExamDropdownItemLabel}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Race')}
        valueText={candidateProfileDetails?.race}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<IExamMasterCodeItem, false, false, undefined>
              label={t('admin->>UserAccountManagement.Race')}
              placeholder="Select race"
              id="input-select-race"
              name="race"
              control={formHook.control}
              error={!!formHook.formState.errors?.race}
              helperText={formHook.formState.errors?.race?.message?.toString()}
              options={iExamsData ? iExamsData.race : []}
              defaultValue={raceDefaultObject}
              getOptionLabel={getIExamDropdownItemLabel}
              isOptionEqualToValue={getIExamMasterCodeSelected}
              isRequired
            />
          )
        }
      />
      <NationalityDetails
        form={formHook}
        isEditState={isEditState}
        candidateProfileDetails={candidateProfileDetails}
        prStatus={prStatus}
        setPrStatus={setPrStatus}
        citizenshipType={citizenshipType}
        setCitizenshipType={setCitizenshipType}
      />
      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.Occupation')}
        allHide
        valueText={candidateProfileDetails?.occupation}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<LookupListApiItem, false, false, undefined>
              label={t('profile->>UserAccountManagement.Occupation')}
              placeholder="Select occupation"
              control={formHook.control}
              name="occupation"
              id="input-select-occupation"
              error={!!formHook.formState.errors?.occupation}
              helperText={formHook.formState.errors?.occupation?.message?.toString()}
              options={iExamsData ? iExamsData.occupation : []}
              defaultValue={occupationDefaultObject}
              getOptionLabel={(name: LookupListApiItem) => name.description}
              isOptionEqualToValue={(option, value) => `${option.id}` === `${value.id}`}
            />
          )
        }
      />

      <ToggleDisplayEditFormItem
        isEditState={isEditState}
        labelText={t('admin->>UserAccountManagement.School')}
        valueText={candidateProfileDetails?.school}
        containerClassName={classes.formElementContainer}
        EditComponent={
          formHook && (
            <ControlledAutocomplete<LookupListItem, false, false, undefined>
              label={t('admin->>UserAccountManagement.School')}
              isRequired
              control={formHook?.control}
              name="school"
              id="input-select-school"
              error={!!formHook.formState.errors?.school}
              helperText={formHook?.formState.errors?.school?.message?.toString()}
              loading={isLoadingOrganisation}
              loadingError={!!errorOrganisation}
              loadingErrorOnReFetch={refetchOrganisation}
              options={filteredDataOrganisationWithoutMOESchool || []}
              defaultValue={schoolDefaultObject}
              disabled={isMOESchool}
              getOptionLabel={({ name }) => name}
              placeholder="Select school"
              isOptionEqualToValue={(option, value) => `${option.id}` === `${value?.id}`}
            />
          )
        }
      />
    </Box>
  );
};
export default CandidatePersonalParticulars;
